import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ZodType, ZodTypeDef } from "zod";
import { ApiError, fetchFastApi } from "../api/fastapi";
import { useAuth } from "../contexts/authContext";
import {
  cursorToSend,
  listingQuery,
  mergeDeltaImports,
} from "../lib/optroImportDelta";
import {
  applyStatusToJob,
  isPendingImportId,
  mergeImportsForPlaceholders,
  mergeListedImports,
  newlyFinishedImportIds,
  OptroImportKind,
  PENDING_IMPORT_PREFIX,
  pickActiveImport,
  readDismissedImportIds,
  trackedFromListing,
  TrackedOptroImport,
  writeImportDismissed,
} from "../lib/optroImportJobs";
import {
  OptroImportItem,
  OptroImportJob,
  OptroImportJobSchema,
  OptroImportListingResponseSchema,
  OptroImportPartialEnqueueSchema,
  StartImportResponseSchema,
} from "../models/audit/optroSync";

const POLL_INTERVAL_MS = 2000;
const IDLE_POLL_INTERVAL_MS = 60000;
const MAX_POLL_INTERVAL_MS = 30000;
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

interface OptroImportJobsConfig<
  TStartItem,
  TSummary extends { externalRef: string },
> {
  kind: OptroImportKind;
  // Whether this user can use the registry listing. When false nothing is
  // polled (the listing would 403 on every cycle).
  enabled: boolean;
  // "/controls/external" or "/work-steps/external".
  basePath: string;
  // Plural noun for toasts: "controls", "work steps".
  noun: string;
  toRequestItem: (item: TStartItem) => Record<string, unknown>;
  toOptimisticItem: (item: TStartItem) => OptroImportItem;
  summaryOf: (item: TStartItem) => TSummary;
  // Parses a listed item's `display` back into the summary it started with.
  summarySchema: ZodType<TSummary, ZodTypeDef, unknown>;
}

export interface OptroImportTracker<TStartItem, TSummary> {
  jobs: TrackedOptroImport<TSummary>[];
  activeJob: TrackedOptroImport<TSummary> | null;
  panelVisible: boolean;
  pollingFailed: boolean;
  // Increments each time an import finishes while tracked here.
  completedImportTick: number;
  // Every tracked import merged, for the placeholder rows.
  placeholderJob: OptroImportJob | null;
  placeholderSummaries: TSummary[];
  startImport: (items: TStartItem[]) => void;
  openImport: (id: string) => void;
  dismissImport: (id: string) => void;
  retryPolling: () => void;
  // Polls the import listing right away (Background Tasks' refresh button).
  refreshImports: () => void;
  // Marks a view that shows imports as on screen (the controls and work steps
  // lists, Background Tasks while open, the progress card while visible),
  // which polls right away and keeps the loop going. Returns the call that
  // unmarks it.
  registerInterest: () => () => void;
}

// Tracks every Optro import of one kind for the current user: the ones
// listed by the import registry (so they survive a refresh) plus any this
// tab started that the registry doesn't list yet. Checks the listing once on
// load (so a running import's card comes back after a reload), then polls
// only while a view that shows imports is on screen: every 2s while one is
// running, every 60s otherwise. With no such view, or in a hidden tab, it
// doesn't poll.
export const useOptroImportJobs = <
  TStartItem,
  TSummary extends { externalRef: string },
>(
  config: OptroImportJobsConfig<TStartItem, TSummary>,
): OptroImportTracker<TStartItem, TSummary> => {
  const { workspace } = useAuth();
  const workspaceId = workspace?.id ?? null;
  const { enabled } = config;
  const configRef = useRef(config);
  configRef.current = config;

  const [jobs, setJobs] = useState<TrackedOptroImport<TSummary>[]>([]);
  const jobsRef = useRef(jobs);
  const [openedId, setOpenedId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() =>
    readDismissedImportIds(config.kind),
  );
  const dismissedIdsRef = useRef(dismissedIds);
  const [seenRunningIds, setSeenRunningIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [pollingFailed, setPollingFailed] = useState(false);
  const [completedImportTick, setCompletedImportTick] = useState(0);
  // Bumped to poll right away (after a start, on retry, when a view that shows
  // imports appears, when the tab is shown again with one on screen).
  const [pollNonce, setPollNonce] = useState(0);
  // How many views that show imports are on screen (registerInterest). While
  // none is, the loop doesn't continue.
  const interestCountRef = useRef(0);
  const pendingCounterRef = useRef(0);
  // Imports whose completion already bumped completedImportTick.
  const tickedIdsRef = useRef<Set<string>>(new Set());
  // The cursor the last listing handed back. Null asks for a full load, which
  // carries display summaries; with a cursor the listing sends only what
  // changed since, without display.
  const cursorRef = useRef<string | null>(null);
  // When the last full load (a request sent without a cursor) completed.
  // Polling with a cursor falls back to a full load once it is old enough.
  const lastFullLoadAtRef = useRef<number | null>(null);

  const applyJobs = useCallback((next: TrackedOptroImport<TSummary>[]) => {
    const finished = newlyFinishedImportIds(jobsRef.current, next);
    finished.forEach((id) => tickedIdsRef.current.add(id));
    jobsRef.current = next;
    setJobs(next);
    const running = next
      .filter((tracked) => !tracked.job.isDone)
      .map((tracked) => tracked.id);
    if (running.length > 0) {
      setSeenRunningIds((current) => {
        if (running.every((id) => current.has(id))) return current;
        const updated = new Set(current);
        running.forEach((id) => updated.add(id));
        return updated;
      });
    }
    if (finished.length > 0) setCompletedImportTick((tick) => tick + 1);
  }, []);

  const resetTracker = useCallback(() => {
    jobsRef.current = [];
    cursorRef.current = null;
    lastFullLoadAtRef.current = null;
    setJobs([]);
    setOpenedId(null);
    setPollingFailed(false);
  }, []);

  // A new workspace starts from an empty list.
  useEffect(() => {
    resetTracker();
  }, [workspaceId, resetTracker]);

  // Switching the tracker off (a live flag or permission change) stops the
  // polling, so the imports it was showing would otherwise stay on screen
  // indefinitely. Only a real true -> false transition clears: `enabled` may
  // read false while the flags and permissions load, and that must not reset
  // anything. Switching back on repopulates through the first poll.
  const wasEnabledRef = useRef(enabled);
  useEffect(() => {
    if (wasEnabledRef.current && !enabled) resetTracker();
    wasEnabledRef.current = enabled;
  }, [enabled, resetTracker]);

  useEffect(() => {
    if (!workspaceId || !enabled) return;
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let consecutiveFailures = 0;
    // Set when the listing answers 403/404 (no access, or the flag is off):
    // later cycles skip it and track this tab's imports by status only.
    let listingUnavailable = false;
    // Set when the last listing left a full load owed (a delta named an import
    // only a full load can bring in, or a snapshot answered a delta): it is
    // fetched on the running interval, not the idle one.
    let fullLoadOwed = false;
    // The first cycle of a run always polls: it is the check on load, or
    // whatever restarted the loop. Later ones are scheduled, and poll only
    // while a view that shows imports is on screen in a visible tab, so a
    // timer that outlives the view sends nothing.
    let firstCycle = true;

    const pollLocalOnly = async (
      targets: TrackedOptroImport<TSummary>[],
    ): Promise<boolean> => {
      let anySucceeded = false;
      for (const target of targets) {
        try {
          const response = await fetchFastApi({
            method: "GET",
            path: `${configRef.current.basePath}/import/${encodeURIComponent(target.id)}/status`,
          });
          if (cancelled) return anySucceeded;
          const status = OptroImportJobSchema.parse(response);
          anySucceeded = true;
          applyJobs(
            jobsRef.current.map((tracked) =>
              tracked.id === target.id
                ? { ...tracked, job: applyStatusToJob(tracked.job, status) }
                : tracked,
            ),
          );
        } catch (error) {
          console.error("Failed to poll Optro import status:", error);
        }
      }
      return anySucceeded;
    };

    const localOnlyRunning = () =>
      jobsRef.current.filter(
        (tracked) =>
          tracked.localOnly &&
          !tracked.job.isDone &&
          !isPendingImportId(tracked.id),
      );

    const cycle = async () => {
      if (
        !firstCycle &&
        (document.visibilityState === "hidden" ||
          interestCountRef.current === 0)
      ) {
        return;
      }
      firstCycle = false;
      const current = configRef.current;
      let listed = false;
      if (!listingUnavailable) {
        try {
          const sentCursor = cursorToSend(
            cursorRef.current,
            lastFullLoadAtRef.current,
            Date.now(),
          );
          const response = await fetchFastApi({
            method: "GET",
            path: `${current.basePath}/imports`,
            query: listingQuery(sentCursor),
          });
          if (cancelled) return;
          const parsed = OptroImportListingResponseSchema.parse(response);
          if (sentCursor === null) lastFullLoadAtRef.current = Date.now();
          const listedJobs = parsed.imports.filter(
            (listedJob) => listedJob.kind === current.kind,
          );
          if (parsed.snapshot === false) {
            const merged = mergeDeltaImports(
              jobsRef.current,
              listedJobs,
              current.summarySchema,
            );
            // No changed jobs hands back the tracked array itself: nothing to
            // apply, so nothing re-renders.
            if (merged.jobs !== jobsRef.current) applyJobs(merged.jobs);
            fullLoadOwed = merged.needsFullLoad;
            cursorRef.current = merged.needsFullLoad
              ? null
              : (parsed.cursor ?? null);
          } else {
            const fromServer = listedJobs.map((listedJob) =>
              trackedFromListing(listedJob, current.summarySchema),
            );
            applyJobs(mergeListedImports(jobsRef.current, fromServer));
            // A snapshot answering a delta request (the cursor was stale) was
            // asked without display summaries, so the next poll is a full
            // load that carries them.
            fullLoadOwed = sentCursor !== null;
            cursorRef.current =
              sentCursor === null ? (parsed.cursor ?? null) : null;
          }
          listed = true;
        } catch (error) {
          if (
            error instanceof ApiError &&
            (error.status === 403 || error.status === 404)
          ) {
            listingUnavailable = true;
            fullLoadOwed = false;
          } else {
            console.error("Failed to list Optro imports:", error);
          }
        }
      }
      if (cancelled) return;

      // Imports the listing doesn't cover (it failed or is unavailable) fall
      // back to the per-import status endpoint. The backend records an import
      // before it starts one, so a started import is listed as soon as the
      // listing works; local-only means the start request is still in flight
      // or the listing endpoint is unavailable.
      const uncovered = localOnlyRunning();
      const statusPolled =
        uncovered.length > 0 ? await pollLocalOnly(uncovered) : false;
      if (cancelled) return;

      // An unavailable listing with nothing to fall back on is not a failure.
      const healthy =
        listed ||
        statusPolled ||
        (listingUnavailable && uncovered.length === 0);
      consecutiveFailures = healthy ? 0 : consecutiveFailures + 1;
      if (healthy) setPollingFailed(false);
      const anyRunning = listingUnavailable
        ? localOnlyRunning().length > 0
        : jobsRef.current.some((tracked) => !tracked.job.isDone);
      // While an import is running, repeated failures raise pollingFailed
      // (the card offers Retry). The loop is not stopped: the tracker now
      // outlives any one route, so it keeps polling at the slowest backoff
      // and recovers by itself once the listing answers, which also clears
      // the flag. Idle, nothing on screen shows a failure, so it just backs
      // off.
      if (anyRunning && consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
        setPollingFailed(true);
      }
      // Backoff never goes faster than a healthy cycle would: it stops at
      // the running cap (30s) while an import runs or a full load is owed and
      // at the idle interval (60s) otherwise.
      const pollQuickly = anyRunning || fullLoadOwed;
      // The loop only continues while a view that shows imports is on screen
      // (registerInterest), running import or not. Otherwise this cycle was
      // the one-off check on load and the loop stops; such a view mounting
      // restarts it. A hidden tab stops too, and showing it again restarts
      // the loop if such a view is mounted (the visibility effect below).
      if (document.visibilityState === "hidden") return;
      if (interestCountRef.current === 0) return;
      const maxBackoff = pollQuickly
        ? MAX_POLL_INTERVAL_MS
        : IDLE_POLL_INTERVAL_MS;
      const delay =
        consecutiveFailures > 0
          ? Math.min(POLL_INTERVAL_MS * 2 ** consecutiveFailures, maxBackoff)
          : pollQuickly
            ? POLL_INTERVAL_MS
            : IDLE_POLL_INTERVAL_MS;
      timeoutId = setTimeout(() => {
        void cycle();
      }, delay);
    };

    void cycle();
    return () => {
      cancelled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [workspaceId, enabled, pollNonce, applyJobs]);

  // The loop stops while the tab is hidden. Showing it again polls right away
  // when a view that shows imports is mounted, and the loop carries on from
  // there.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        interestCountRef.current > 0
      ) {
        setPollNonce((nonce) => nonce + 1);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const registerInterest = useCallback(() => {
    interestCountRef.current += 1;
    setPollNonce((nonce) => nonce + 1);
    return () => {
      interestCountRef.current -= 1;
    };
  }, []);

  // Pending ids are never persisted: the counter restarts on a reload, so a
  // stored "pending:1" would hide a later import's card.
  const setDismissed = useCallback((id: string, dismissed: boolean) => {
    if (!isPendingImportId(id)) {
      writeImportDismissed(configRef.current.kind, id, dismissed);
    }
    const current = dismissedIdsRef.current;
    if (current.has(id) === dismissed) return;
    const updated = new Set(current);
    if (dismissed) updated.add(id);
    else updated.delete(id);
    dismissedIdsRef.current = updated;
    setDismissedIds(updated);
  }, []);

  // Give a pending import its real id. A poll may already have listed the
  // real id during the POST; then the pending entry is dropped rather than
  // renamed, so ids stay unique. Either way the import keeps the pending
  // entry's instance key, so its card is not remounted. A dismissal made
  // while pending carries over. If the listed copy is already done, no poll
  // will see it finish, so the completion is announced here.
  const replaceId = useCallback(
    (fromId: string, toId: string) => {
      const existing = jobsRef.current.find((tracked) => tracked.id === toId);
      const pending = jobsRef.current.find((tracked) => tracked.id === fromId);
      if (existing) {
        applyJobs(
          jobsRef.current.flatMap((tracked) => {
            if (tracked.id === fromId) return [];
            if (tracked.id === toId && pending) {
              return [
                {
                  ...tracked,
                  instanceKey: pending.instanceKey,
                  summaries:
                    pending.summaries.length > tracked.summaries.length
                      ? pending.summaries
                      : tracked.summaries,
                },
              ];
            }
            return [tracked];
          }),
        );
        if (pending && existing.job.isDone && !tickedIdsRef.current.has(toId)) {
          tickedIdsRef.current.add(toId);
          setCompletedImportTick((tick) => tick + 1);
        }
      } else {
        applyJobs(
          jobsRef.current.map((tracked) =>
            tracked.id === fromId ? { ...tracked, id: toId } : tracked,
          ),
        );
      }
      if (dismissedIdsRef.current.has(fromId)) {
        setDismissed(fromId, false);
        setDismissed(toId, true);
      }
      setOpenedId((current) => (current === fromId ? toId : current));
      setPollNonce((nonce) => nonce + 1);
    },
    [applyJobs, setDismissed],
  );

  const dropImport = useCallback(
    (id: string) => {
      applyJobs(jobsRef.current.filter((tracked) => tracked.id !== id));
      setOpenedId((current) => (current === id ? null : current));
      // Poll again right away with a fresh failure count.
      setPollNonce((nonce) => nonce + 1);
    },
    [applyJobs],
  );

  const startImport = useCallback(
    (items: TStartItem[]) => {
      const current = configRef.current;
      pendingCounterRef.current += 1;
      const pendingId = `${PENDING_IMPORT_PREFIX}${pendingCounterRef.current}`;
      applyJobs([
        {
          id: pendingId,
          instanceKey: pendingId,
          createdAt: new Date().toISOString(),
          job: {
            id: "",
            isDone: false,
            items: items.map(current.toOptimisticItem),
          },
          summaries: items.map(current.summaryOf),
          localOnly: true,
        },
        ...jobsRef.current,
      ]);
      setOpenedId(pendingId);
      setPollingFailed(false);

      void (async () => {
        try {
          const response = await fetchFastApi({
            method: "POST",
            path: `${current.basePath}/import`,
            body: { items: items.map(current.toRequestItem) },
          });
          const parsed = StartImportResponseSchema.parse(response);
          replaceId(pendingId, parsed.importId);
        } catch (error) {
          if (error instanceof ApiError) {
            const partial = OptroImportPartialEnqueueSchema.safeParse(
              error.detailData,
            );
            if (partial.success) {
              const { importId, enqueued, failed } = partial.data;
              if (enqueued === 0) {
                // Nothing started: there is nothing on the server to track.
                toast.error("Failed to start import");
                dropImport(pendingId);
                return;
              }
              toast.error(
                `${failed} of ${enqueued + failed} ${current.noun} failed to start importing. The rest will continue.`,
              );
              replaceId(pendingId, importId);
              return;
            }
            toast.error(error.getUserMessage());
          } else {
            console.error("Failed to start Optro import:", error);
            toast.error("Failed to start import");
          }
          dropImport(pendingId);
        }
      })();
    },
    [applyJobs, dropImport, replaceId],
  );

  const openImport = useCallback(
    (id: string) => {
      setDismissed(id, false);
      setOpenedId(id);
    },
    [setDismissed],
  );

  const dismissImport = useCallback(
    (id: string) => {
      setDismissed(id, true);
      setOpenedId((current) => (current === id ? null : current));
    },
    [setDismissed],
  );

  // Restarts the poll loop with an immediate cycle, clearing any failure
  // streak, and resyncs from a full load. Serves both the card's Retry and an
  // on-demand refresh.
  const retryPolling = useCallback(() => {
    cursorRef.current = null;
    lastFullLoadAtRef.current = null;
    setPollingFailed(false);
    setPollNonce((nonce) => nonce + 1);
  }, []);

  const activeJob = useMemo(
    () => pickActiveImport(jobs, openedId, dismissedIds, seenRunningIds),
    [jobs, openedId, dismissedIds, seenRunningIds],
  );

  // Imports that finished before this visit are left out: their rows are
  // already in the list, so they need no placeholders.
  const placeholders = useMemo(
    () =>
      mergeImportsForPlaceholders(
        jobs.filter(
          (tracked) => !tracked.job.isDone || seenRunningIds.has(tracked.id),
        ),
      ),
    [jobs, seenRunningIds],
  );

  return {
    jobs,
    activeJob,
    panelVisible: activeJob !== null && !dismissedIds.has(activeJob.id),
    pollingFailed,
    completedImportTick,
    placeholderJob: placeholders.job,
    placeholderSummaries: placeholders.summaries,
    startImport,
    openImport,
    dismissImport,
    retryPolling,
    refreshImports: retryPolling,
    registerInterest,
  };
};
